/**
 * How narrow a widget may get before it stops being readable (TV1-2291 · §1).
 *
 * The engine is becoming responsive, and the question "how small is too small?"
 * has to be answered in a unit that survives a viewport change. The three
 * obvious models all answer it in COLUMNS, and all three fail for the same
 * reason: a column is not a fixed size. `minW: 3` on a 12-column grid is 480px
 * at 1920, 250px at 1000 and 97px on a phone — the same declared constraint
 * meaning something different at every width, which is precisely the bug.
 *
 * So the minimum is stated in CSS px, once per widget TYPE, and the resolver
 * converts it to columns at render time against the width it actually has.
 * Nothing is stored: the responsive behaviour is a pure function of
 * `( stored layout, container width, per-type minimum )`, so there is no schema
 * change, no per-breakpoint layout, and nothing to migrate.
 *
 * ⚠️ PLACEMENT — this lives at the `WidgetLayout/` level, NOT under
 * `Widgets/WidgetBuilder/`. `defaultDimensions` sits under the builder for
 * historical reasons and `autoGrid` already reaches into it, but that dependency
 * points the wrong way: layout data consumed by the RESOLVER has no business
 * living inside the widget-authoring form. New sizing data goes here;
 * `defaultDimensions` can follow later as a pure relocation.
 */

import { WIDGET_GAP_PX } from './gridGeometry';
import type { WidgetType } from './Widgets/types';

/**
 * Minimum width, in CSS px, each widget type's content needs to stay legible.
 *
 * These are facts about the content, not about the window: a bar chart's axis
 * labels collide at ~280px and a KPI's number wraps at ~140px whatever the
 * viewport is doing. That is the whole reason the unit is pixels.
 *
 * ⚠️ EXHAUSTIVE ON PURPOSE — `Record<WidgetType, number>`, never `Partial`. A
 * widget type that gains a renderer but no minimum must be a COMPILE error,
 * because the only alternative is a runtime fallback that silently hands the
 * new type somebody else's legibility floor. This is the OPPOSITE call from
 * `defaultDimensions` (`Widgets/WidgetBuilder/WidgetConstructors.ts`), which is
 * deliberately sparse — mobile genuinely has no `scatterChart` — and therefore
 * needs a hard fallback in the resolver. Do not copy that shape here.
 *
 * TV1-2291 (t21) — `spacer` and `heading` have LANDED, and the mechanism above is
 * what put them here: widening `WidgetType` broke this object until both were
 * given a minimum.
 *
 * TV1-2291 (t25) — so have the four READMODEL-backed types, and the mechanism
 * worked a second time: widening the union for `count` / `alert` / `list` /
 * `progress` broke this object until each was given the minimum the SDD (§1)
 * declared for it. Resist the urge to "fix" a future break by loosening the type.
 */
export const MIN_CONTENT_PX: Record<WidgetType, number> = {
  // A spacer has no content, so it has no legibility floor — the ONLY zero in the
  // table, and it is meaningful rather than a placeholder. `minColsFor` floors the
  // result at 1 regardless, so a spacer still occupies a whole cell (it has to:
  // occupying cells is its entire job — see `SpacerWidget`).
  spacer: 0,
  kpi: 140,
  // TV1-2291 (t25) — a `count` is a KPI whose source is a readmodel count, so it
  // shares the KPI's floor to the pixel: one number plus a caption, and the number
  // wraps below ~140px whatever produced it.
  count: 140,
  // An `alert` is that same number PLUS a severity treatment beside it, so it needs
  // a little more room than a bare count before the two collide.
  alert: 170,
  // A progress bar has to be long enough to read as a proportion AND carry its
  // "12 / 40" readout; below ~190px the bar is a stub and the readout wraps under it.
  progress: 190,
  donutChart: 200,
  pieChart: 200,
  // A section title plus its rule; below ~200px the title truncates to an ellipsis
  // and the divider stops reading as one.
  heading: 200,
  text: 240,
  areaChart: 280,
  barChart: 280,
  lineChart: 280,
  scatterChart: 280,
  // A `list` row is a primary field plus secondary ones on the same line. It sits
  // just under `table` (320): it renders FEWER columns, but each row is prose-like
  // rather than a grid cell, so it truncates sooner per column.
  list: 300,
  table: 320,
  criticalStock: 320
};

/**
 * How many columns `type` needs at this column width, clamped into `[1, cols]`.
 *
 * The two clamps are not decoration. The floor keeps a widget on the grid at all
 * (a `spacer`, minimum 0, is a valid single cell); the ceiling is what makes a
 * widget whose minimum exceeds the ENTIRE grid render full width rather than
 * spilling off the canvas — on a phone that is most of them.
 *
 * @param unscaledColPx Column width in CSS px with the toolbar zoom DIVIDED OUT
 *   (`geometry.colPx / scale`). Zoom is a magnification of an unchanged layout:
 *   computing the minimum from the SCALED width would make zooming out re-clamp,
 *   so the report would reflow under the user as they zoom — which is not what a
 *   zoom control means anywhere. The caller owns the division because only the
 *   caller knows the current `scale`.
 */
export const minColsFor = (
  type: WidgetType,
  unscaledColPx: number,
  cols: number,
  // The REPORT's gutter (2026-08-17). Defaults to the engine's so every existing
  // call site and test keeps its meaning; a report that widens its gap widens the
  // minimum with it, which is what keeps `MIN_CONTENT_PX` an honest promise at any
  // gap value rather than only at the default one.
  gapPx: number = WIDGET_GAP_PX
): number => {
  // An element that has not been laid out yet measures 0, and `NaN` arrives the
  // same way. `computeGridGeometry` already guards that with its `widthPx`
  // fallback so this should be unreachable — but if it ever is reached,
  // "impose no minimum" is the safe direction: rendering unclamped is visually
  // wrong and harmless, whereas `x / 0` → `Infinity` → `cols` would blow every
  // widget on the report up to full width.
  if ( !( unscaledColPx > 0 )) return 1;

  // ⚠️ THE GUTTER IS PART OF THE MINIMUM (2026-08-17).
  //
  // `MIN_CONTENT_PX` is a promise about the width the CONTENT gets. Cards are now
  // drawn `WIDGET_GAP_PX` narrower than the cell they own (`widgetRectPx`), so
  // sizing against the raw cell would quietly break that promise by exactly one
  // gutter — a 320px-minimum table would render 308px of content and the guarantee
  // would be false while every test asserting the CELL still passed.
  //
  // So the cell has to be big enough to hold the content AND the gutter. This is
  // the only place that conversion happens, which is why the gap is imported here
  // rather than being folded into the `MIN_CONTENT_PX` numbers — those stay
  // readable as "what this widget needs to be legible", independent of chrome.
  const needed = Math.ceil(( MIN_CONTENT_PX[type] + gapPx ) / unscaledColPx );
  return Math.max( 1, Math.min( needed, cols ));
};
